import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine
} from "recharts";
import { Play, Loader2, Printer, Download, ShieldCheck, AlertTriangle } from "lucide-react";

const PERIODS = ["6M", "1Y", "2Y", "3Y", "5Y"];
const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const money = (n) => `₹${fmt(n)}`;
const pct = (n) => `${Number(n ?? 0).toFixed(2)}%`;

function Metric({ label, value, sub }) {
  return (
    <div className="border border-slate-200 bg-white p-4 rounded-sm">
      <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500">{label}</div>
      <div className="font-mono text-xl font-semibold mt-1 text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="bg-white border border-slate-200 rounded-sm mb-5 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-heading font-bold text-sm uppercase tracking-wide">{title}</h2>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead><tr className="border-b border-slate-200 text-slate-500">
          {headers.map((h) => <th key={h} className="text-left py-2 px-2 whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>{rows.map((r, i) =>
          <tr key={i} className="border-b border-slate-100 last:border-0">
            {r.map((x, j) => <td key={j} className={`py-2 px-2 whitespace-nowrap ${j > 0 ? "font-mono" : ""}`}>{x}</td>)}
          </tr>
        )}</tbody>
      </table>
    </div>
  );
}

export default function Backtest() {
  const [params] = useSearchParams();
  const [strategies, setStrategies] = useState([]);
  const [sid, setSid] = useState(params.get("strategy") || "");
  const [period, setPeriod] = useState("1Y");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState("report");

  useEffect(() => {
    api.get("/strategies").then(r => {
      setStrategies(r.data || []);
      if (!sid && r.data?.length) setSid(r.data[0].id);
    }).catch(() => {});
  }, [sid]);

  const run = async () => {
    if (!sid) return toast.error("Select a strategy");
    setBusy(true);
    setReport(null);
    try {
      const { data } = await api.post("/backtest/run", { strategy_id: sid, period });
      setReport(data);
      toast.success("Real Dhan backtest completed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backtest failed");
    } finally {
      setBusy(false);
    }
  };

  const r = report?.result;
  const s = r?.stats || {};

  const monthlyChart = useMemo(() => r?.monthly_returns || [], [r]);
  const eqChart = useMemo(() => r?.equity_curve || [], [r]);
  const dailyChart = useMemo(() => r?.daily_returns || [], [r]);

  return (
    <Layout>
      <PageHeader
        title="Backtest"
        subtitle="Real Dhan 1-minute historical option replay — Tradetron-style performance, risk, cost and trade analytics"
        right={
          <div className="flex gap-2">
            {report && <button onClick={() => window.print()} className="h-9 px-3 border border-slate-300 bg-white text-xs font-semibold flex items-center gap-2"><Printer className="w-4 h-4"/> Print / PDF</button>}
            <button onClick={run} disabled={busy} className="h-9 px-4 bg-slate-900 text-white text-xs font-semibold flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Play className="w-4 h-4"/>}
              {busy ? "Fetching Dhan Data..." : "Run Real Backtest"}
            </button>
          </div>
        }
      />

      <div className="mb-5 bg-white border border-slate-200 p-4 flex flex-wrap gap-3 items-end no-print">
        <div className="min-w-[260px]">
          <label className="text-[10px] uppercase font-bold text-slate-500">Strategy</label>
          <select value={sid} onChange={e => setSid(e.target.value)} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white">
            <option value="">Select strategy</option>
            {strategies.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Period</label>
          <div className="flex mt-1">
            {PERIODS.map(x => <button key={x} onClick={() => setPeriod(x)} className={`h-10 px-4 border-y border-r first:border-l text-xs font-semibold ${period === x ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{x}</button>)}
          </div>
        </div>
        {r && <div className="text-xs text-slate-500 ml-auto">Data: <b>{r.data_source}</b></div>}
      </div>

      {busy && (
        <div className="bg-white border border-slate-200 p-10 text-center mb-5">
          <Loader2 className="w-7 h-7 animate-spin mx-auto mb-3"/>
          <div className="font-semibold">Downloading / reading Dhan historical data</div>
          <div className="text-xs text-slate-500 mt-1">First run can take longer because 1-minute option data is cached by strike and 30-day chunk.</div>
        </div>
      )}

      {!r && !busy && (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center text-slate-500">
          Select a Strategy and click <b>Run Real Backtest</b>.
        </div>
      )}

      {r && (
        <div id="backtest-report">
          <div className="bg-white border border-slate-200 p-5 mb-5">
            <div className="flex justify-between gap-4">
              <div>
                <div className="text-lg font-bold">{r.strategy?.name || "Strategy"} — Backtest Report</div>
                <div className="text-xs text-slate-500 mt-1">{r.strategy?.type || "Intraday"} · {r.date_range?.from} → {r.date_range?.to} · {s.trading_days} trading days</div>
              </div>
              <div className="text-right text-xs">
                <div className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-1"><ShieldCheck className="w-3 h-3"/> Real historical data</div>
                <div className="mt-2 text-slate-500">{r.execution_model?.lookahead_bias === "none" ? "No look-ahead bias" : ""}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Metric label="Capital Required" value={money(s.initial_capital)} />
            <Metric label="Gross P&L" value={money(s.gross_pnl)} sub="before costs" />
            <Metric label="Gross ROI" value={pct(s.gross_roi_pct)} />
            <Metric label="CAGR Gross" value={pct(s.cagr_gross_pct)} />
            <Metric label="Sharpe Gross" value={fmt(s.sharpe_gross)} />
            <Metric label="Sortino Gross" value={fmt(s.sortino_gross)} />
            <Metric label="Calmar Gross" value={fmt(s.calmar_gross)} />
            <Metric label="Max DD" value={pct(-Math.abs(s.max_drawdown_pct))} sub={money(s.max_drawdown_amount)} />
            <Metric label="Win Rate" value={pct(s.win_rate_pct)} sub={`${s.winning_trades}W / ${s.losing_trades}L`} />
            <Metric label="Expectancy / Trade" value={money(s.expectancy_per_trade)} />
          </div>

          <div className="flex border-b border-slate-200 bg-white mb-5 no-print">
            {["report", "trades", "strategy"].map(x =>
              <button key={x} onClick={() => setTab(x)} className={`px-5 py-3 text-xs font-bold uppercase ${tab === x ? "border-b-2 border-slate-900" : "text-slate-500"}`}>{x === "report" ? "Report" : x === "trades" ? "Trades & Data" : "Strategy"}</button>
            )}
          </div>

          {tab === "report" && <>
            <Section title="Full Statistics">
              <Table headers={["Metric", "Value"]} rows={[
                ["Capital Required", money(s.initial_capital)],
                ["Total Gross P&L", money(s.gross_pnl)],
                ["Total Net P&L", money(s.net_pnl)],
                ["Gross ROI", pct(s.gross_roi_pct)],
                ["Net ROI", pct(s.net_roi_pct)],
                ["CAGR", pct(s.cagr_gross_pct)],
                ["Sharpe", fmt(s.sharpe_gross)],
                ["Net Sharpe", fmt(s.sharpe_net)],
                ["Sortino", fmt(s.sortino_gross)],
                ["Calmar", fmt(s.calmar_gross)],
                ["Win Rate", pct(s.win_rate_pct)],
                ["Winning / Losing Trades", `${s.winning_trades} / ${s.losing_trades}`],
                ["Trading Days", s.trading_days],
                ["Average Trades / Day", fmt(s.avg_trades_per_day)],
                ["Average Trades / Month", fmt(s.avg_trades_per_month)],
                ["Expectancy / Trade", money(s.expectancy_per_trade)],
                ["Max Profit Day", money(s.max_profit_day)],
                ["Max Loss Day", money(s.max_loss_day)],
                ["Average P&L / Day", money(s.avg_pnl_day)],
                ["Average Profit Day", money(s.avg_profit_day)],
                ["Average Loss Day", money(s.avg_loss_day)],
                ["Daily Return SD", pct(s.daily_return_sd_pct)],
                ["Monthly P&L SD", money(s.monthly_pnl_sd)],
                ["Win / Loss Streak", `${s.win_streak} / ${s.loss_streak}`],
                ["Average Holding", `${fmt(s.avg_holding_minutes)} min`],
                ["Max Drawdown", `${pct(-Math.abs(s.max_drawdown_pct))} (${money(s.max_drawdown_amount)})`],
                ["Profit Factor", fmt(s.profit_factor)],
                ["Recovery Factor", fmt(s.recovery_factor)],
                ["Ulcer Index", fmt(s.ulcer_index)],
                ["Pain Index", fmt(s.pain_index)],
              ]}/>
            </Section>

            <Section title="Equity Curve & Drawdown">
              <div className="h-[330px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={eqChart}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="date" minTickGap={35}/>
                    <YAxis/>
                    <Tooltip/>
                    <Area type="monotone" dataKey="gross_equity" name="Gross Equity" fillOpacity={0.12} strokeWidth={2}/>
                    <Line type="monotone" dataKey="drawdown" name="Drawdown" strokeWidth={1.5} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <div className="grid md:grid-cols-2 gap-5">
              <Section title="Monthly Returns">
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyChart}>
                      <CartesianGrid strokeDasharray="3 3"/>
                      <XAxis dataKey="month"/>
                      <YAxis/>
                      <Tooltip/>
                      <ReferenceLine y={0}/>
                      <Bar dataKey="pnl" name="P&L"/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table headers={["Month","Trades","P&L","ROI"]} rows={monthlyChart.map(x => [x.month,x.trades,money(x.pnl),pct(x.roi_pct)])}/>
              </Section>

              <Section title="Daily Return Distribution">
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyChart.slice(-100)}>
                      <CartesianGrid strokeDasharray="3 3"/>
                      <XAxis dataKey="date" hide/>
                      <YAxis/>
                      <Tooltip/>
                      <ReferenceLine y={0}/>
                      <Bar dataKey="pnl" name="Daily P&L"/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Metric label="Best Day" value={money(s.max_profit_day)}/>
                  <Metric label="Worst Day" value={money(s.max_loss_day)}/>
                </div>
              </Section>
            </div>

            <Section title="Monte-Carlo Robustness">
              <div className="grid md:grid-cols-5 gap-3">
                <Metric label="Simulations" value={fmt(r.monte_carlo?.simulations)}/>
                <Metric label="Prob. Profitable" value={pct(r.monte_carlo?.prob_profitable_pct)}/>
                <Metric label="Terminal P5" value={money(r.monte_carlo?.terminal_p5)}/>
                <Metric label="Terminal Median" value={money(r.monte_carlo?.terminal_median)}/>
                <Metric label="Terminal P95" value={money(r.monte_carlo?.terminal_p95)}/>
              </div>
              <p className="text-xs text-slate-500 mt-3">Block bootstrap of daily return path. This tests sensitivity to the sequence of realised daily returns; it is not a guarantee of future performance.</p>
            </Section>

            <div className="grid md:grid-cols-2 gap-5">
              <Section title="P&L Attribution — by Leg">
                <Table headers={["Leg","Trades","P&L","Win %"]} rows={(r.leg_attribution || []).map(x => [x.leg,x.trades,money(x.pnl),pct(x.win_pct)])}/>
              </Section>
              <Section title="Timing Edge — Day of Week">
                <Table headers={["Day","Trades","Total","Avg","Win %"]} rows={(r.weekday || []).map(x => [x.day,x.trades,money(x.pnl),money(x.avg),pct(x.win_pct)])}/>
              </Section>
            </div>

            <Section title="Top 5 Drawdowns">
              <Table headers={["Start","Trough","Depth","%","Days"]} rows={(r.drawdowns || []).map(x => [
                x.start_index, x.trough_index, money(x.depth), pct(-Math.abs(x.pct)), x.days
              ])}/>
            </Section>

            <Section title="Risk Flags & Hypotheses">
              {(r.risk_flags || []).length === 0
                ? <div className="text-sm text-emerald-700 flex items-center gap-2"><ShieldCheck className="w-4 h-4"/> No automatic risk flags fired.</div>
                : <div className="space-y-3">{r.risk_flags.map((x,i) => <div key={i} className="border-l-2 border-amber-500 pl-3"><div className="font-semibold text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500"/>{x.title}</div><div className="text-xs text-slate-500 mt-1">{x.detail}</div></div>)}</div>}
            </Section>

            <Section title="Cost Lab">
              <div className="grid md:grid-cols-4 gap-3 mb-4">
                <Metric label="Gross P&L" value={money(s.gross_pnl)}/>
                <Metric label="Total Costs" value={money(-r.costs.total_costs)}/>
                <Metric label="Net P&L" value={money(s.net_pnl)}/>
                <Metric label="Net ROI" value={pct(s.net_roi_pct)}/>
              </div>
              <Table headers={["Cost","Amount"]} rows={[
                ["Brokerage",money(r.costs.brokerage)],
                ["Slippage",money(r.costs.slippage)],
                ["STT",money(r.costs.stt)],
                ["Exchange Transaction",money(r.costs.exchange_txn)],
                ["GST",money(r.costs.gst)],
                ["Stamp Duty",money(r.costs.stamp_duty)],
                ["SEBI",money(r.costs.sebi)],
                ["Total Costs",money(r.costs.total_costs)],
                ["Orders",r.costs.orders],
                ["Turnover",money(r.costs.turnover)],
              ]}/>
              <div className="text-[11px] text-slate-500 mt-3">Default Indian F&O cost profile. These are modelled costs, not broker-generated contract notes.</div>
            </Section>


            <Section title="Trade Quality & Advanced Analytics">
              <div className="grid md:grid-cols-4 gap-3 mb-4">
                <Metric label="Avg Winner" value={money(s.average_winning_trade)} />
                <Metric label="Avg Loser" value={money(s.average_losing_trade)} />
                <Metric label="Best Trade" value={money(s.largest_winning_trade)} />
                <Metric label="Worst Trade" value={money(s.largest_losing_trade)} />
                <Metric label="Trade Win Streak" value={fmt(s.trade_win_streak)} />
                <Metric label="Trade Loss Streak" value={fmt(s.trade_loss_streak)} />
                <Metric label="Profit Factor" value={fmt(s.profit_factor)} />
                <Metric label="Recovery Factor" value={fmt(s.recovery_factor)} />
              </div>
            </Section>

            <div className="grid md:grid-cols-2 gap-5">
              <Section title="Exit Reason Analysis">
                <Table
                  headers={["Exit Reason","Trades","P&L","Win %"]}
                  rows={(r.exit_reason_analysis || []).map(x => [
                    x.reason,
                    x.trades,
                    money(x.pnl),
                    pct(x.win_pct)
                  ])}
                />
              </Section>

              <Section title="Entry Time Analysis">
                <Table
                  headers={["Time","Trades","P&L","Avg","Win %"]}
                  rows={(r.time_of_day || []).map(x => [
                    x.time,
                    x.trades,
                    money(x.pnl),
                    money(x.avg),
                    pct(x.win_pct)
                  ])}
                />
              </Section>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Section title="CE / PE Analysis">
                <Table
                  headers={["Option","Trades","P&L","Win %"]}
                  rows={(r.option_type_analysis || []).map(x => [
                    x.option_type,
                    x.trades,
                    money(x.pnl),
                    pct(x.win_pct)
                  ])}
                />
              </Section>

              <Section title="Strike / OTM Selection">
                <Table
                  headers={["Selection","Trades","P&L","Win %"]}
                  rows={(r.strike_selection_analysis || []).map(x => [
                    x.selection,
                    x.trades,
                    money(x.pnl),
                    pct(x.win_pct)
                  ])}
                />

                {(r.strike_analysis || []).length > 0 && (
                  <div className="mt-4">
                    <Table
                      headers={["Strike","Trades","P&L","Win %"]}
                      rows={(r.strike_analysis || []).slice(0, 30).map(x => [
                        x.strike,
                        x.trades,
                        money(x.pnl),
                        pct(x.win_pct)
                      ])}
                    />
                  </div>
                )}
              </Section>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Section title="Expiry Analysis">
                <Table
                  headers={["Expiry","Trades","P&L","Win %"]}
                  rows={(r.expiry_analysis || []).map(x => [
                    x.expiry,
                    x.trades,
                    money(x.pnl),
                    pct(x.win_pct)
                  ])}
                />
              </Section>

              <Section title="Holding Time Analysis">
                <Table
                  headers={["Holding","Trades","P&L"]}
                  rows={(r.holding_time_analysis || []).map(x => [
                    x.bucket,
                    x.trades,
                    money(x.pnl)
                  ])}
                />
              </Section>
            </div>

            <Section title="Advanced Risk Distribution">
              <div className="grid md:grid-cols-5 gap-3">
                <Metric
                  label="Daily SD"
                  value={pct(r.advanced_risk?.daily_return_std_pct)}
                />
                <Metric
                  label="Skewness"
                  value={fmt(r.advanced_risk?.skewness)}
                />
                <Metric
                  label="Excess Kurtosis"
                  value={fmt(r.advanced_risk?.excess_kurtosis)}
                />
                <Metric
                  label="VaR 95% Daily"
                  value={pct(r.advanced_risk?.var_95_daily_pct)}
                />
                <Metric
                  label="CVaR 95% Daily"
                  value={pct(r.advanced_risk?.cvar_95_daily_pct)}
                />
              </div>
            </Section>

            <Section title="Additional Risk Data">
              <div className="grid md:grid-cols-2 gap-4 text-xs">
                <div className="border border-slate-200 p-3">
                  <div className="font-bold">VIX Regime</div>
                  <div className="text-slate-500 mt-1">
                    {r.advanced_data?.vix_regime
                      ? "Available"
                      : "Not available from current Dhan replay"}
                  </div>
                  <div className="text-slate-400 mt-1">
                    {r.advanced_data?.vix_note}
                  </div>
                </div>

                <div className="border border-slate-200 p-3">
                  <div className="font-bold">Margin Utilisation</div>
                  <div className="text-slate-500 mt-1">
                    {r.advanced_data?.margin_utilisation == null
                      ? "Not available"
                      : pct(r.advanced_data.margin_utilisation)}
                  </div>
                  <div className="text-slate-400 mt-1">
                    {r.advanced_data?.margin_note}
                  </div>
                </div>
              </div>
            </Section>

            <Section title="Data & Execution Method">
              <Table headers={["Item","Value"]} rows={[
                ["Data source",r.data_source],
                ["Data quality",r.data_quality],
                ["Period",`${r.date_range?.from} → ${r.date_range?.to}`],
                ["Timeframe","1 minute"],
                ["Signal",r.execution_model?.signal],
                ["Entry",r.execution_model?.entry],
                ["Target / SL",r.execution_model?.target_sl],
                ["Same candle",r.execution_model?.same_candle_target_and_sl],
                ["Look-ahead bias",r.execution_model?.lookahead_bias],
                ["Lot size",r.lot_size_source],
                ["Cache",r.cache_note],
              ]}/>
            </Section>
          </>}

          {tab === "trades" && (
            <Section title={`Trade Ledger — ${r.trade_count_total} total`}>
              <Table headers={["Entry","Exit","Reason","Legs","P&L"]} rows={(r.trades || []).map(t => [
                `${t.entry_date} ${t.entry_time}`,
                `${t.exit_date} ${t.exit_time}`,
                t.reason,
                t.legs?.map(l => `${l.side} ${l.option_type} ${l.strike} × ${l.qty}`).join(" | "),
                money(t.pnl)
              ])}/>
            </Section>
          )}

          {tab === "strategy" && (
            <>
              <Section title="Strategy Configuration">
                <Table headers={["Field","Value"]} rows={[
                  ["Name",r.strategy?.name],
                  ["Underlying",r.strategy?.underlying],
                  ["Type",r.strategy?.type],
                  ["Capital",money(r.strategy?.capital)],
                  ["Entry Time",r.strategy?.entry_time],
                  ["Exit Time",r.strategy?.exit_time],
                  ["Indicators",(r.strategy?.indicators || []).map(x => x.indicator).join(", ") || "None"],
                  ["Positions",`${r.strategy?.positions?.length || 0} leg(s)`],
                ]}/>
              </Section>
              <Section title="Position Legs">
                <Table headers={["Side","Option","Strike","Expiry","Lots","Target","SL"]} rows={(r.strategy?.positions || []).map(p => [
                  p.side,p.option_type,p.strike_selection,p.expiry,p.lots,
                  `${p.target_type} ${p.target_value || ""}`,
                  `${p.sl_type} ${p.sl_value || ""}`
                ])}/>
              </Section>
            </>
          )}

          <div className="text-[10px] text-slate-400 pb-10">
            Kiran Algo real-data backtest. Historical results are based on Dhan's 1-minute rolling expired-options data and the stated execution model. They are not a promise of future performance.
          </div>
        </div>
      )}
    </Layout>
  );
}
